import { createFileRoute } from "@tanstack/react-router";

import { ServicePageLayout } from "../components/site/ServicePageLayout";
import { ClientsMarquee } from "../components/shared/ClientsMarquee";
import { ContactForm } from "../components/home/ContactForm";
import { Faq } from "../components/shared/Faq";
import { ContextHero } from "../components/context/ContextHero";
import { ContextAdvantages } from "../components/context/ContextAdvantages";
import { ContextSetup } from "../components/context/ContextSetup";
import { ContextManage } from "../components/context/ContextManage";
import { ContextCampaigns } from "../components/context/ContextCampaigns";
import { ContextAudit } from "../components/context/ContextAudit";
import { ContextPricing } from "../components/context/ContextPricing";
import { ContextProcess } from "../components/context/ContextProcess";
import { ContextCases } from "../components/context/ContextCases";
import { ContextLinks } from "../components/context/ContextLinks";
import { buildMeta, breadcrumbJsonLd, serviceJsonLd, SITE_URL } from "../lib/site";

const title = "Настройка и ведение Яндекс Директа — стоимость | HAY.agency";
const description =
  "Настройка и ведение рекламы в Яндекс Директе: поиск, РСЯ, ретаргетинг, товарные кампании, аудит, аналитика и оптимизация. Рассчитаем стоимость под задачи бизнеса.";

const faqItems = [
  {
    q: "Сколько стоит настройка и ведение Яндекс Директа?",
    a: "От 68 000 ₽ в месяц: в стоимость входят настройка или аудит аккаунта, еженедельная оптимизация и отчётность. Итоговую цену фиксируем после брифа. Рекламный бюджет оплачивается отдельно и напрямую в кабинет.",
  },
  {
    q: "Что входит в ежемесячное ведение?",
    a: "Работа с поисковыми запросами и минус-словами, чистка площадок РСЯ, корректировка ставок и стратегий, тесты объявлений, ретаргетинг и ежемесячный отчёт с выводами.",
  },
  {
    q: "Какой минимальный рекламный бюджет нужен?",
    a: "Универсальной цифры нет: минимум зависит от цены клика в нише и количества конверсий для обучения стратегий. На брифе считаем прогноз и говорим прямо, если бюджета не хватит на значимый тест.",
  },
  {
    q: "Через сколько будут первые заявки?",
    a: "Обычно в первые дни после запуска, но выводы по эффективности делаем после накопления статистики — как правило, за первый месяц активной оптимизации.",
  },
  {
    q: "Работаете ли вы с уже существующим аккаунтом?",
    a: "Да. Начинаем с аудита: структура, семантика, площадки, ставки и аналитика. Часто выгоднее исправить аккаунт и сохранить статистику, чем перезапускать с нуля.",
  },
  {
    q: "Что входит в отчётность?",
    a: "Расход, клики, обращения и цена обращения по кампаниям и направлениям, что тестировали и что планируем дальше. При сквозной аналитике — данные вплоть до сделок в CRM.",
  },
  {
    q: "Даёте ли вы гарантию результата?",
    a: "Мы не обещаем фиксированное число заявок или конкретный ROI: результат зависит от спроса, конкуренции и качества обработки обращений. Гарантируем прозрачный процесс, доступ к кабинетам и отчётность по фактическим данным.",
  },
];

export const Route = createFileRoute("/context")({
  head: () => ({
    ...buildMeta({
      title,
      description,
      path: "/context",
      image: `${SITE_URL}/og/services/context.webp`,
      imageWidth: 1200,
      imageHeight: 630,
      imageAlt: "Настройка и ведение Яндекс Директа в HAY.agency",
      locale: "ru_RU",
    }),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "WebPage",
              "@id": `${SITE_URL}/context#webpage`,
              url: `${SITE_URL}/context`,
              name: title,
              description,
              inLanguage: "ru-RU",
              isPartOf: { "@id": `${SITE_URL}/#website` },
              breadcrumb: { "@id": `${SITE_URL}/context#breadcrumbs` },
              mainEntity: { "@id": `${SITE_URL}/context#service` },
            },
            serviceJsonLd({
              name: "Настройка и ведение Яндекс Директа",
              description,
              path: "/context",
              id: `${SITE_URL}/context#service`,
              serviceType: "Контекстная реклама в Яндекс Директе",
              providerId: `${SITE_URL}/#organization`,
              inGraph: true,
            }),
            breadcrumbJsonLd(
              [
                ["Главная", "/"],
                ["Яндекс Директ", "/context"],
              ],
              { id: `${SITE_URL}/context#breadcrumbs`, inGraph: true },
            ),
            {
              "@type": "FAQPage",
              "@id": `${SITE_URL}/context#faq`,
              mainEntity: faqItems.map((item) => ({
                "@type": "Question",
                name: item.q,
                acceptedAnswer: { "@type": "Answer", text: item.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: Page,
});


function Page() {
  return (
    <ServicePageLayout breadcrumb="Яндекс Директ" jsonLd={false}>
      <ContextHero />
      <ClientsMarquee bare />
      <ContextAdvantages />
      <ContextSetup />
      <ContextManage />
      <ContextCampaigns />
      <ContextAudit />
      <ContextProcess />
      <ContextCases />
      <ContextPricing />
      <Faq
        compact
        title="Частые вопросы о Яндекс Директе"
        lead="Стоимость, бюджеты, сроки и отчётность — коротко."
        items={faqItems}
      />
      <ContextLinks />
      <ContactForm />
    </ServicePageLayout>
  );
}
